/*
 * <bulk-order-form> — HoReCa carton picker.
 *
 * Each row is one SKU wired to a Shopify variant. Quantities are always entered
 * in CARTONS. The section's cart unit decides the cart line quantity: 1 per
 * carton when the variant is itself a carton (the GMB* trade SKUs), or
 * cartons x case size when the variant is a single pack.
 *
 * Adds to the normal Shopify cart via routes.cart_add_url, so Shopflo picks it
 * up like any other cart.
 */
(function () {
  'use strict';

  var CART_ADD = (window.routes && window.routes.cart_add_url) || '/cart/add';
  var CART_URL = (window.routes && window.routes.cart_url) || '/cart';

  function inr(n) {
    try {
      return '₹' + Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });
    } catch (e) {
      return '₹' + Math.round(n);
    }
  }

  /**
   * Hand off to Shopflo, which is the cart and checkout provider.
   *
   * The theme's own cart/drawer/notification checkout buttons all call the global
   * `handleFloCheckoutBtn()` that the Shopflo bridge injects — the native Shopify
   * checkout button is commented out in main-cart-footer.liquid. We follow the
   * same route, with the ladder degrading to Shopflo's object API, then its cart,
   * then a plain /cart redirect (which the bridge also intercepts).
   *
   * `mode` is 'checkout' or 'cart'.
   */
  function handoff(mode) {
    var S = window.Shopflo;
    var wantCheckout = mode !== 'cart';

    if (wantCheckout) {
      // Respect Shopflo's own kill switch rather than forcing its checkout.
      var enabled = !S || S.isFloCheckoutEnabled === undefined || !!S.isFloCheckoutEnabled;
      if (enabled) {
        if (typeof window.handleFloCheckoutBtn === 'function') {
          window.handleFloCheckoutBtn(); return true;
        }
        if (S && typeof S.openFloCheckout === 'function') { S.openFloCheckout(); return true; }
        if (S && typeof S.openCheckout === 'function') { S.openCheckout(); return true; }
      }
    }

    if (typeof window.handleFloCartBtn === 'function') { 
      document.dispatchEvent(new CustomEvent('shopflo-event:add-to-cart'));
      return true; 
    }
    if (S && typeof S.openFloCart === 'function') { S.openFloCart(); return true; }

    window.location.href = CART_URL;
    return true;
  }

  class BulkOrderForm extends HTMLElement {
    connectedCallback() {
      this.rows = [...this.querySelectorAll('[data-bulk-row]')].map(function (el) {
        return {
          el: el,
          variantId: el.dataset.variantId,
          caseSize: Math.max(1, parseInt(el.dataset.caseSize, 10) || 1),
          cartonPrice: parseFloat(el.dataset.cartonPrice || '0'), // rupees per carton
          available: el.dataset.available === 'true',
          maxStock: el.dataset.maxStock ? parseInt(el.dataset.maxStock, 10) : null,
          input: el.querySelector('[data-bulk-count]'),
          dec: el.querySelector('[data-bulk-dec]'),
          inc: el.querySelector('[data-bulk-inc]'),
          packsLine: el.querySelector('[data-bulk-packs]'),
        };
      }).filter(function (r) { return r.variantId && r.input; });

      this.cartUnit = this.dataset.cartUnit === 'cartons' ? 'cartons' : 'packs';
      this.afterAdd = this.dataset.afterAdd === 'cart' ? 'cart' : 'checkout';
      this.priceNote = this.dataset.priceNote || '';
      this.barCartons = this.querySelector('[data-bulk-total-cartons]');
      this.barCartonWord = this.querySelector('[data-bulk-carton-word]');
      this.barPacks = this.querySelector('[data-bulk-total-packs]');
      this.barValue = this.querySelector('[data-bulk-total-value]');
      this.submit = this.querySelector('[data-bulk-submit]');
      this.toast = this.querySelector('[data-bulk-toast]');

      var self = this;
      this.rows.forEach(function (r) {
        if (r.dec) r.dec.addEventListener('click', function () { self.bump(r, -1); });
        if (r.inc) r.inc.addEventListener('click', function () { self.bump(r, 1); });
        r.input.addEventListener('input', function () { self.sanitise(r); self.render(); });
        r.input.addEventListener('blur', function () { self.sanitise(r); self.render(); });
      });
      if (this.submit) this.submit.addEventListener('click', this.placeOrder.bind(this));

      this.render();
    }

    qtyOf(r) { return Math.max(0, parseInt(r.input.value, 10) || 0); }

    /**
     * Cap a row at whole cartons of available stock, when stock is tracked.
     * Shopify's inventory is counted in whatever the variant is, so divide by
     * the case size only when the variant is a pack.
     */
    maxCartons(r) {
      if (r.maxStock == null) return null;
      return this.cartUnit === 'cartons'
        ? r.maxStock
        : Math.floor(r.maxStock / r.caseSize);
    }

    sanitise(r) {
      var v = Math.max(0, parseInt(String(r.input.value).replace(/\D/g, ''), 10) || 0);
      var cap = this.maxCartons(r);
      if (cap != null) v = Math.min(v, Math.max(0, cap));
      r.input.value = v ? String(v) : '0';
    }

    bump(r, delta) {
      r.input.value = String(this.qtyOf(r) + delta);
      this.sanitise(r);
      this.render();
    }

    render() {
      var cartons = 0, packs = 0, value = 0, self = this;
      this.rows.forEach(function (r) {
        var q = self.qtyOf(r);
        cartons += q;
        packs += q * r.caseSize;
        value += q * r.cartonPrice;
        if (r.packsLine) {
          r.packsLine.textContent = 'CARTONS · '
            + (q * r.caseSize).toLocaleString('en-IN') + ' PACKS';
        }
        var cap = self.maxCartons(r);
        if (r.dec) r.dec.disabled = q <= 0;
        if (r.inc) r.inc.disabled = !r.available || (cap != null && q >= cap);
      });

      if (this.barCartons) this.barCartons.textContent = String(cartons);
      if (this.barCartonWord) this.barCartonWord.textContent = cartons === 1 ? 'carton' : 'cartons';
      if (this.barPacks) this.barPacks.textContent = packs.toLocaleString('en-IN') + ' PACKS';
      if (this.barValue) {
        this.barValue.textContent = value > 0
          ? inr(value) + (this.priceNote ? ' ' + this.priceNote : '')
          : '';
      }
      if (this.submit) this.submit.disabled = cartons === 0;
      this.totals = { cartons: cartons, packs: packs, value: value };
    }

    showToast(msg) {
      if (!this.toast) return;
      clearTimeout(this._tt);
      this.toast.textContent = msg;
      this.toast.hidden = false;
      var self = this;
      this._tt = setTimeout(function () { self.toast.hidden = true; }, 3200);
    }

    placeOrder() {
      var self = this;
      var items = [];
      this.rows.forEach(function (r) {
        var q = self.qtyOf(r);
        if (q > 0) {
          items.push({
            id: Number(r.variantId),
            quantity: self.cartUnit === 'cartons' ? q : q * r.caseSize,
          });
        }
      });
      if (!items.length) { this.showToast('add some cartons first! 🌶'); return; }

      this.submit.disabled = true;
      var original = this.submit.textContent;
      this.submit.textContent = 'ADDING…';

      fetch(CART_ADD, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items }),
      })
        .then(function (res) {
          return res.json().then(function (body) {
            if (!res.ok) throw new Error(body && body.description ? body.description : 'cart error');
            return body;
          });
        })
        .then(function () {
          self.submit.textContent = 'OPENING CHECKOUT…';
          // The bridge loads async; if it isn't ready yet the ladder in handoff()
          // ends at a /cart redirect, which Shopflo also intercepts.
          handoff(self.afterAdd);
          // Re-enable in case Shopflo opens an overlay and the shopper closes it.
          setTimeout(function () {
            self.submit.disabled = false;
            self.submit.textContent = original;
          }, 4000);
        })
        .catch(function (err) {
          self.submit.disabled = false;
          self.submit.textContent = original;
          // Surface the real reason — usually an out-of-stock cap.
          self.showToast(err.message || 'could not add to cart, please try again');
        });
    }
  }

  if (!window.customElements.get('bulk-order-form')) {
    window.customElements.define('bulk-order-form', BulkOrderForm);
  }
})();
